import { hosts } from '../../config';
import { BaseClient } from '../BaseClient/BaseClient';
import type {
  AutoProcessAtsVacancyHandler,
  CallJobFieldSmartWriterHandler,
  CreateEnrichedVacancyHandler,
  CreateProcessVacancyHandler,
  DeleteEnrichedVacancyHandler,
  GenerateEnrichedVersionHandler,
  GenerateJobPreviewHandler,
  GetEnrichedVacancyHandler,
  JobImprovedNestedInput,
  JobImprovedPatchInput,
  JobImprovedUpdateInput,
  ListCombinedVacanciesHandler,
  PatchEnrichedVacancyHandler,
  ProcessAtsVacancyHandler,
  ProcessCampaignAIHandler,
  ProcessVacancyRequest,
  UpdateEnrichedVacancyHandler,
} from './ReachoutClient.types';

export class ReachoutClient extends BaseClient {
  constructor() {
    super();
    this.listCombinedVacancies = this.listCombinedVacancies.bind(this);
    this.processVacancy = this.processVacancy.bind(this);
    this.createJobImproved = this.createJobImproved.bind(this);
    this.getJobImproved = this.getJobImproved.bind(this);
    this.updateJobImproved = this.updateJobImproved.bind(this);
    this.patchJobImproved = this.patchJobImproved.bind(this);
    this.deleteJobImproved = this.deleteJobImproved.bind(this);
    this.processAtsVacancy = this.processAtsVacancy.bind(this);
    this.processCampaignAI = this.processCampaignAI.bind(this);
    this.autoProcessAtsVacancy = this.autoProcessAtsVacancy.bind(this);
    this.callJobFieldSmartWriter = this.callJobFieldSmartWriter.bind(this);
  }

  listCombinedVacancies: ListCombinedVacanciesHandler = (searchTerm, type) => {
    return this.request({
      url: `${hosts.reachoutApi()}/combined-vacancies/`,
      method: 'GET',
      params: { ...(searchTerm && { starts_with: searchTerm }), ...(type && { type }) },
    });
  };

  processVacancy: CreateProcessVacancyHandler = (data: ProcessVacancyRequest) => {
    const formData = new FormData();
    formData.append('input_type', data.input_type);

    if (data.input_type === 'text') {
      formData.append('input_text', data.input_text);
    } else {
      formData.append('vacancy_file', data.vacancy_file);
    }

    return this.request({
      url: `${hosts.reachoutApi()}/process-vacancy/`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  createJobImproved: CreateEnrichedVacancyHandler = (data: JobImprovedNestedInput) => {
    return this.request({
      url: `${hosts.reachoutApi()}/job-improved/`,
      method: 'POST',
      data,
    });
  };

  getJobImproved: GetEnrichedVacancyHandler = async (id: number) => {
    return this.request({
      url: `${hosts.reachoutApi()}/job-improved/${id}/`,
      method: 'GET',
    });
  };

  updateJobImproved: UpdateEnrichedVacancyHandler = (id: number, data: JobImprovedUpdateInput) => {
    return this.request({
      url: `${hosts.reachoutApi()}/job-improved/${id}/`,
      method: 'PUT',
      data,
    });
  };

  patchJobImproved: PatchEnrichedVacancyHandler = (id: number, data: JobImprovedPatchInput) => {
    return this.request({
      url: `${hosts.reachoutApi()}/job-improved/${id}/`,
      method: 'PATCH',
      data,
    });
  };

  deleteJobImproved: DeleteEnrichedVacancyHandler = (id: number) => {
    return this.request({
      url: `${hosts.reachoutApi()}/job-improved/${id}/`,
      method: 'DELETE',
    });
  };

  processAtsVacancy: ProcessAtsVacancyHandler = (atsId: string) => {
    return this.request({
      url: `${hosts.reachoutApi()}/process-ats-vacancy/${atsId}/`,
      method: 'POST',
    });
  };

  processCampaignAI: ProcessCampaignAIHandler = (campaignId: string) => {
    return this.request({
      url: `${hosts.reachoutApi()}/process-campaign-ai/${campaignId}/`,
      method: 'POST',
    });
  };

  autoProcessAtsVacancy: AutoProcessAtsVacancyHandler = (payload) => {
    return this.request({
      url: `${hosts.reachoutApi()}/auto-process-ats-vacancy/`,
      method: 'POST',
      data: payload,
    });
  };

  generateJobPreview: GenerateJobPreviewHandler = (payload) => {
    return this.request({
      url: `${hosts.reachoutApi()}/generate-job-preview/`,
      method: 'POST',
      data: payload,
    });
  };

  generateEnrichedVersion: GenerateEnrichedVersionHandler = (payload) => {
    return this.request({
      url: `${hosts.reachoutApi()}/generate-job-improved/`,
      method: 'POST',
      data: payload,
    });
  };

  callJobFieldSmartWriter: CallJobFieldSmartWriterHandler = async (payload) => {
    const { data } = await this.request({
      url: `${hosts.reachoutApi()}/job-field-smart-writer/`,
      method: 'POST',
      data: payload,
    });

    return data;
  };
}

export const reachoutClient = new ReachoutClient();
